/**
 * Normalize single-case (ALL-CAPS or all-lowercase) names and street
 * addresses to title case.
 *
 * Mixed-case input is someone's deliberate choice (McDonald, van der Berg)
 * and is always left alone.
 */

/** Generational suffixes and clinical credentials that stay fully uppercase. */
const KEEP_UPPER_NAME = new Set([
  'II', 'III', 'IV',
  'MD', 'DO', 'DDS', 'DVM', 'OD', 'RN', 'NP', 'PA',
]);

/** Mixed-case credential exceptions (matched case-insensitively). */
const SPECIAL_NAME: { [key: string]: string } = {
  PHD: 'PhD',
  PHARMD: 'PharmD',
};

/**
 * Address tokens that stay fully uppercase. State codes are deliberately
 * excluded: LA/DE/etc. appear inside street names (LA CIENEGA, DE ANZA) far
 * more often than bare state codes appear in an address line.
 */
const KEEP_UPPER_ADDRESS = new Set(['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW', 'PO', 'RR']);

/** True when the value contains letters in only one case (all caps or all lowercase). */
export const isUniformCase = (value: string): boolean =>
  /[A-Za-z]/.test(value) && (!/[a-z]/.test(value) || !/[A-Z]/.test(value));

const capitalizeWord = (word: string): string => {
  const upper = word.toUpperCase();
  if (SPECIAL_NAME[upper]) {
    return SPECIAL_NAME[upper];
  }
  if (KEEP_UPPER_NAME.has(upper)) {
    return upper;
  }
  const lower = word.toLowerCase();
  // Mc only — a Mac rule would break Machado, Macy, etc.
  if (lower.startsWith('mc') && lower.length > 2) {
    return 'Mc' + lower.charAt(2).toUpperCase() + lower.slice(3);
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/** Title-case with word boundaries at spaces, hyphens, apostrophes, periods. */
export const toNameCase = (value: string): string =>
  value
    .split(/([\s\-'’.])/)
    .map(part => (/^[\s\-'’.]$/.test(part) ? part : capitalizeWord(part)))
    .join('');

/** Title-case an address line, keeping directionals and PO/RR uppercase. */
export const toAddressCase = (value: string): string =>
  value
    .split(/(\s+)/)
    .map(token => {
      const bare = token.replace(/[^0-9A-Za-z]/g, '');
      // Unit/street designators like 2B or 100A (ordinals like 9TH still lowercase).
      if (/^\d+[A-Za-z]$/.test(bare)) {
        return token.toUpperCase();
      }
      return KEEP_UPPER_ADDRESS.has(bare.replace(/[^A-Za-z]/g, '').toUpperCase())
        ? token.toUpperCase()
        : toNameCase(token);
    })
    .join('');

export type NameCaseMode = 'name' | 'address';

export const normalizeUniformCase = (value: string, mode: NameCaseMode = 'name'): string =>
  isUniformCase(value) ? (mode === 'address' ? toAddressCase(value) : toNameCase(value)) : value;
